import { GetAvailableUserForTripResponse, SortDirectionEnum, User } from '@/models/gen/graphql';
import QueryDropdown, { QueryDropdownProps } from '@/components/QueryDropdown';
import { QueryInputType, queryInput } from '@/utils';
import { ReactNode, useCallback } from 'react';

import { DropdownItem } from '@/components/Dropdown';
import getAvailableUsersForTrip from '@/api/services/trips/getAvailableUsersForTrip';
import { getSelectUserFilter } from '@/api/services/users/searchUsers';
import md5 from 'md5';
import useSession from '@/state/session';

export type DriverDropdownProps = Omit<QueryDropdownProps, 'query'> & {
  airports?: string[];
};

const DriverDropdown = ({ name = 'driver', airports = [], ...props }: DriverDropdownProps): ReactNode => {
  const getItems = useCallback(async (): Promise<DropdownItem[]> => {
    const driverRoleId = useSession.getState().state?.config?.driverRoleId || null;
    const res = await getSelectUserFilter(airports, {
      roleId: queryInput(driverRoleId, QueryInputType.OR),
      firstName: queryInput(null, QueryInputType.DEFAULT, SortDirectionEnum.Asc, 0),
      lastName: queryInput(null, QueryInputType.DEFAULT, SortDirectionEnum.Asc, 1),
    });
    return (
      res?.map?.((user: User): DropdownItem => ({ label: user.fullName, value: user.id, keywords: [...user.fullName.split(' ')] })) || []
    );
  }, [airports]);

  return (
    <QueryDropdown
      {...props}
      name={`${name}_${airports.join('|') || 'ALL'}`}
      query={getItems}
      options={{ ...(props?.options || {}), locale: { ...(props?.options?.locale || {}), 'Select...': 'Driver' } }}
    />
  );
};

export type AssignDriverDropdownProps = Omit<QueryDropdownProps, 'query'> & {
  airportCode: string;
  scheduled: string;
  actual: string;
  tripId: string;
  details: User;
};

export const AssignDriverDropdown = ({
  name = 'driver',
  airportCode,
  scheduled,
  actual,
  tripId,
  details,
  ...props
}: AssignDriverDropdownProps): ReactNode => {
  const getItems = useCallback(async (): Promise<DropdownItem[]> => {
    const res = await getAvailableUsersForTrip({ airportCode, scheduled, actual, tripId });
    const result =
      res.map?.(
        (user: GetAvailableUserForTripResponse): DropdownItem => ({
          label: [user?.employeeId || '', user.firstName, user.lastName].join(' '),
          value: user.userId,
          keywords: [user.firstName, user.lastName, user.employeeId],
        })
      ) || [];
    if (details?.id) {
      const index = result.findIndex((item: DropdownItem): boolean => item.value === details.id);
      const detailsItem: DropdownItem = {
        label: [details?.employeeId || '', details?.firstName || '', details?.lastName || ''].join(' '),
        value: details?.id,
        keywords: [details?.firstName || '', details?.lastName || '', details?.employeeId || ''].filter(Boolean),
      };
      if (index !== -1) {
        result[index] = detailsItem;
      } else {
        result.unshift(detailsItem);
      }
    }
    return result;
  }, [airportCode, scheduled, actual, tripId, details]);

  return (
    <QueryDropdown
      {...props}
      name={`${name}_${md5(airportCode + scheduled + actual)}`}
      query={getItems}
      options={{ ...(props.options || {}), locale: { 'No Options': 'No Available Drivers', ...(props.options?.locale || {}) } }}
    />
  );
};

export default DriverDropdown;
